import type { AIModel, Attachment } from '@/types';
import { getProviderLogoOverride } from '@/lib/modelSettings';

export const GEMMA4_ONNX_MODEL_ID = 'gemma4-e2b-onnx';
export const GEMMA4_ONNX_HOSTED_PATH = 'https://models.mapleos.ai/gemma-4-E2B-it-ONNX/';
const GEMMA4_ACK_KEY = 'gemma4_ack';
const GEMMA4_THINKING_KEY = 'gemma4_thinking_enabled';

type GemmaContentPart = {
  type: 'text' | 'image' | 'audio';
  text?: string;
  source?: string;
};

type GemmaMessage = {
  role: 'user' | 'assistant' | 'system';
  content: GemmaContentPart[];
};

export type Gemma4OnnxProgress = {
  stage: 'idle' | 'starting' | 'processor' | 'tokenizer' | 'model' | 'ready' | 'error';
  message: string;
  progress: number | null;
  isReady: boolean;
  hasError: boolean;
  error?: string;
  raw?: unknown;
};

type GemmaGenerateInput = {
  messages: Array<{
    role: 'user' | 'assistant' | 'system';
    content: string | Array<{ type: string; text?: string; image_url?: { url: string; detail?: string } }>;
    attachments?: Attachment[];
  }>;
  config?: {
    max_tokens?: number;
    temperature?: number;
    top_p?: number;
    enableThinking?: boolean;
  };
};

type GemmaLoadOptions = {
  modelId?: string;
  dtype?: 'q4f16';
  device?: 'webgpu';
};

type ProgressListener = (info: Gemma4OnnxProgress) => void;

type ResolvedModelSource = {
  modelId: string;
  displaySource: string;
  remoteHost?: string;
  remotePathTemplate?: string;
};

const defaultProgress = (): Gemma4OnnxProgress => ({
  stage: 'idle',
  message: 'Gemma 4 ONNX is idle.',
  progress: null,
  isReady: false,
  hasError: false,
});

export function isElectronEnvironment() {
  return typeof window !== 'undefined' && Boolean(window.electronAPI?.isElectron);
}

export function isGemma4OnnxModelId(modelId: string | null | undefined) {
  return modelId === GEMMA4_ONNX_MODEL_ID;
}

export function supportsGemma4Thinking(model: Pick<AIModel, 'id' | 'provider'> | string | null | undefined) {
  if (!model) {
    return false;
  }

  if (typeof model === 'string') {
    return isGemma4OnnxModelId(model);
  }

  return model.provider === 'transformers' || isGemma4OnnxModelId(model.id);
}

export function loadGemma4ThinkingEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }

  return localStorage.getItem(GEMMA4_THINKING_KEY) === 'true';
}

export function saveGemma4ThinkingEnabled(value: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(GEMMA4_THINKING_KEY, value ? 'true' : 'false');
}

export function createGemma4OnnxModel(): AIModel {
  return {
    id: GEMMA4_ONNX_MODEL_ID,
    name: 'Gemma 4 E2B',
    provider: 'transformers',
    description: 'Gemma 4 E2B',
    capabilities: ['text', 'image', 'audio'],
    tier: 'free',
    apiName: GEMMA4_ONNX_HOSTED_PATH,
    is_active: true,
    logo_url: getProviderLogoOverride('transformers') || '/os/assets/icons/gemma-color.svg',
  };
}

export function appendGemma4OnnxModel(models: AIModel[]) {
  if (models.some((model) => model.id === GEMMA4_ONNX_MODEL_ID)) {
    return models;
  }

  return [...models, createGemma4OnnxModel()];
}

export async function loadGemma4OnnxAcknowledged() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const desktopValue = await window.electronAPI?.loadDesktopConfig?.(GEMMA4_ACK_KEY);
    if (typeof desktopValue === 'boolean') {
      return desktopValue;
    }
  } catch (error) {
    console.warn('[Gemma4Onnx] Failed to load Electron acknowledgement state:', error);
  }

  return localStorage.getItem(GEMMA4_ACK_KEY) === 'true';
}

export async function saveGemma4OnnxAcknowledged(value: boolean) {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(GEMMA4_ACK_KEY, value ? 'true' : 'false');

  try {
    await window.electronAPI?.saveDesktopConfig?.(GEMMA4_ACK_KEY, value);
  } catch (error) {
    console.warn('[Gemma4Onnx] Failed to persist Electron acknowledgement state:', error);
  }
}

function getGemma4OnnxSourceCandidates(explicitModelId?: string) {
  const candidates = new Set<string>();

  if (explicitModelId?.trim()) {
    candidates.add(explicitModelId.trim());
  }

  candidates.add(GEMMA4_ONNX_HOSTED_PATH);
  candidates.add(GEMMA4_ONNX_HOSTED_PATH.replace(/\/$/, ''));

  return Array.from(candidates);
}

function resolveModelSource(candidate: string): ResolvedModelSource {
  try {
    const url = new URL(candidate);
    const modelId = url.pathname.replace(/^\/+|\/+$/g, '');

    if (!modelId) {
      return {
        modelId: candidate,
        displaySource: candidate,
      };
    }

    return {
      modelId,
      displaySource: candidate,
      remoteHost: `${url.origin}/`,
      remotePathTemplate: '{model}/',
    };
  } catch {
    return {
      modelId: candidate,
      displaySource: candidate,
    };
  }
}

function shouldFallbackToTokenizer(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || '');
  return [
    'No `image_processor_type` or `feature_extractor_type` found in the config',
    "Unknown image_processor_type: 'Gemma4ImageProcessor'",
    "Unknown feature_extractor_type: 'Gemma4AudioFeatureExtractor'",
    "Unknown video_processor_type: 'Gemma4VideoProcessor'",
  ].some((fragment) => message.includes(fragment));
}

function formatGemmaThinkingResponse(thinking: string, answer: string) {
  const cleanedThinking = thinking.trim();
  const cleanedAnswer = answer.trim();

  if (!cleanedThinking) {
    return cleanedAnswer;
  }

  return [
    '## Thinking',
    '',
    cleanedThinking,
    '',
    '## Answer',
    '',
    cleanedAnswer || 'No final answer returned.',
  ].join('\n');
}

function stripGemmaControlTokens(text: string) {
  return text
    .replace(/<\|start_of_turn\|>/g, '')
    .replace(/<\|end_of_turn\|>/g, '')
    .replace(/<\|assistant\|>/g, '')
    .replace(/<\|user\|>/g, '')
    .replace(/<\|system\|>/g, '')
    .replace(/<\|think\|>/g, '')
    .replace(/<bos>/gi, '')
    .replace(/<eos>/gi, '')
    .replace(/<turn>/gi, '\n<TURN>')
    .replace(/<channel[^>]*>/gi, '\n<CHANNEL>\n')
    .replace(/<start_of_image>/gi, '[image]')
    .replace(/<end_of_image>/gi, '')
    .trim();
}

function extractGemmaModelTurns(text: string) {
  const normalized = stripGemmaControlTokens(text);
  const turnMatches = Array.from(normalized.matchAll(/<TURN>\s*(system|user|model)\s*([\s\S]*?)(?=<TURN>\s*(?:system|user|model)|$)/gi));

  if (turnMatches.length === 0) {
    return [];
  }

  return turnMatches
    .map((match) => ({
      role: match[1].toLowerCase(),
      content: match[2].trim(),
    }))
    .filter((turn) => turn.content.length > 0);
}

function cleanGemmaSegment(text: string) {
  return text
    .replace(/<TURN>/g, '')
    .replace(/<CHANNEL>/g, '')
    .replace(/^['"]|['"]$/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function extractGemmaThinkingFallback(text: string) {
  const turns = extractGemmaModelTurns(text);
  const lastModelTurn = [...turns].reverse().find((turn) => turn.role === 'model');
  const modelContent = lastModelTurn?.content || stripGemmaControlTokens(text);
  const channelParts = modelContent.split(/<CHANNEL>/i).map(cleanGemmaSegment).filter(Boolean);

  if (channelParts.length >= 2) {
    return {
      thinking: channelParts[0],
      answer: channelParts.slice(1).join('\n\n'),
    };
  }

  return {
    thinking: '',
    answer: cleanGemmaSegment(modelContent),
  };
}

function cleanGemmaTextResponse(text: string) {
  return extractGemmaThinkingFallback(text).answer;
}

class Gemma4OnnxManager {
  private processor: any = null;
  private tokenizer: any = null;
  private model: any = null;
  private loadPromise: Promise<void> | null = null;
  private progress: Gemma4OnnxProgress = defaultProgress();
  private listeners = new Set<ProgressListener>();

  getProgress() {
    return this.progress;
  }

  isReady() {
    return Boolean((this.processor || this.tokenizer) && this.model);
  }

  onProgress(listener: ProgressListener) {
    this.listeners.add(listener);
  }

  offProgress(listener: ProgressListener) {
    this.listeners.delete(listener);
  }

  private emit(progress: Gemma4OnnxProgress) {
    this.progress = progress;
    this.listeners.forEach((listener) => listener(progress));

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('mapleos:gemma4-onnx-progress', { detail: progress }));
    }
  }

  private update(partial: Partial<Gemma4OnnxProgress>) {
    this.emit({
      ...this.progress,
      ...partial,
    });
  }

  async load(options: GemmaLoadOptions = {}) {
    if (typeof navigator === 'undefined' || !('gpu' in navigator)) {
      throw new Error('WebGPU is required for Gemma 4 ONNX but is not available in this environment.');
    }

    if (this.isReady()) {
      this.emit({
        stage: 'ready',
        message: 'Gemma 4 ONNX is ready.',
        progress: 100,
        isReady: true,
        hasError: false,
      });
      return;
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    const sourceCandidates = getGemma4OnnxSourceCandidates(options.modelId);
    const dtype = options.dtype ?? 'q4f16';
    const device = options.device ?? 'webgpu';

    this.emit({
      stage: 'starting',
      message: 'Preparing Gemma 4 ONNX runtime...',
      progress: 0,
      isReady: false,
      hasError: false,
    });

    this.loadPromise = (async () => {
      try {
        const transformers: any = await import('@huggingface/transformers');
        if (transformers.env) {
          transformers.env.useBrowserCache = false;
        }
        const AutoProcessor = transformers.AutoProcessor;
        const AutoTokenizer = transformers.AutoTokenizer;
        const ModelCtor =
          transformers.Gemma4ForConditionalGeneration ||
          transformers.AutoModelForImageTextToText ||
          transformers.AutoModelForCausalLM;

        if (!ModelCtor || !AutoTokenizer) {
          throw new Error('Transformers.js Gemma 4 ONNX APIs are not available in this build.');
        }

        let activeSource = sourceCandidates[0];
        let lastLoadError: unknown = null;

        const progress_callback = (info: any) => {
          const progress =
            typeof info?.progress === 'number'
              ? Math.max(0, Math.min(100, info.progress))
              : typeof info?.loaded === 'number' && typeof info?.total === 'number' && info.total > 0
                ? Math.max(0, Math.min(100, (info.loaded / info.total) * 100))
                : null;

          const stage = info?.status || info?.file || 'loading';
          const message =
            typeof info?.file === 'string'
              ? `Loading ${info.file}`
              : typeof info?.status === 'string'
                ? String(info.status)
                : 'Loading model files...';

          this.emit({
            stage,
            message,
            progress,
            isReady: false,
            hasError: false,
            raw: info,
          });
        };

        for (const candidate of sourceCandidates) {
          try {
            const resolvedSource = resolveModelSource(candidate);
            activeSource = resolvedSource.displaySource;

            if (resolvedSource.remoteHost) {
              transformers.env.remoteHost = resolvedSource.remoteHost;
              transformers.env.remotePathTemplate = resolvedSource.remotePathTemplate;
              transformers.env.allowRemoteModels = true;
            }

            this.processor = null;
            this.tokenizer = null;

            this.update({
              stage: 'processor',
              message: `Loading processor from ${resolvedSource.displaySource}...`,
              progress: 5,
              isReady: false,
              hasError: false,
            });

            if (AutoProcessor) {
              try {
                this.processor = await AutoProcessor.from_pretrained(resolvedSource.modelId, {
                  progress_callback,
                });
              } catch (processorError) {
                if (!shouldFallbackToTokenizer(processorError)) {
                  throw processorError;
                }

                this.update({
                  stage: 'tokenizer',
                  message: 'Processor metadata missing; falling back to tokenizer-only text mode...',
                  progress: 12,
                  isReady: false,
                  hasError: false,
                });
              }
            }

            if (!this.processor) {
              this.tokenizer = await AutoTokenizer.from_pretrained(resolvedSource.modelId, {
                progress_callback,
              });
            }

            this.update({
              stage: 'model',
              message: `Loading Gemma 4 ONNX weights from ${resolvedSource.displaySource}...`,
              progress: 35,
              isReady: false,
              hasError: false,
            });

            this.model = await ModelCtor.from_pretrained(resolvedSource.modelId, {
              dtype,
              device,
              progress_callback,
            });

            this.emit({
              stage: 'ready',
              message: this.processor
                ? `Gemma 4 ONNX is ready from ${activeSource}.`
                : `Gemma 4 ONNX is ready from ${activeSource} in text-only mode.`,
              progress: 100,
              isReady: true,
              hasError: false,
            });
            return;
          } catch (candidateError) {
            lastLoadError = candidateError;
            this.processor = null;
            this.tokenizer = null;
            this.model = null;
          }
        }

        throw lastLoadError instanceof Error ? lastLoadError : new Error('Failed to load Gemma 4 ONNX.');
      } catch (error: any) {
        this.processor = null;
        this.tokenizer = null;
        this.model = null;
        this.emit({
          stage: 'error',
          message: error?.message || 'Failed to load Gemma 4 ONNX.',
          progress: null,
          isReady: false,
          hasError: true,
          error: error?.message || 'Failed to load Gemma 4 ONNX.',
        });
        throw error;
      } finally {
        this.loadPromise = null;
      }
    })();

    return this.loadPromise;
  }

  async generate(input: GemmaGenerateInput) {
    if (!this.isReady()) {
      throw new Error('Gemma 4 ONNX is not ready yet.');
    }

    if (!this.processor) {
      const hasAttachments = input.messages.some((message) => (message.attachments?.length || 0) > 0);
      if (hasAttachments) {
        throw new Error('This Gemma bundle is running in text-only mode because its processor config is missing. Image and audio inputs are not available.');
      }
    }

    const transformers: any = await import('@huggingface/transformers');
    const enableThinking = Boolean(input.config?.enableThinking);
    const prompt = this.buildPrompt(input.messages, enableThinking);
    const imageSources: string[] = [];
    const audioSources: string[] = [];

    input.messages.forEach((message) => {
      message.attachments?.forEach((attachment) => {
        if (attachment.type === 'image') {
          imageSources.push(attachment.content);
        }
        if (attachment.type === 'audio') {
          audioSources.push(attachment.content);
        }
      });
    });

    const processorInputs = await this.buildProcessorInputs(transformers, prompt, imageSources, audioSources);

    let outputText = '';
    const activeTokenizer = this.processor?.tokenizer ?? this.tokenizer ?? this.processor;
    const streamer = !enableThinking && transformers.TextStreamer && activeTokenizer
      ? new transformers.TextStreamer(activeTokenizer, {
          skip_prompt: true,
          callback_function: (text: string) => {
            outputText += text;
          },
        })
      : undefined;

    const generation = await this.model.generate({
      ...processorInputs,
      max_new_tokens: Math.min(input.config?.max_tokens ?? (enableThinking ? 1024 : 512), enableThinking ? 1024 : 512),
      temperature: input.config?.temperature ?? 0.7,
      top_p: input.config?.top_p ?? 0.95,
      do_sample: true,
      streamer,
    });

    if (!streamer) {
      const decoder = this.processor?.batch_decode?.bind(this.processor) ?? this.tokenizer?.batch_decode?.bind(this.tokenizer);
      outputText = decoder
        ? decoder(generation, { skip_special_tokens: !enableThinking })?.[0] || ''
        : '';
    }

    if (enableThinking) {
      const parseResponse =
        this.processor?.parse_response?.bind(this.processor) ??
        this.processor?.parseResponse?.bind(this.processor) ??
        activeTokenizer?.parse_response?.bind(activeTokenizer) ??
        activeTokenizer?.parseResponse?.bind(activeTokenizer);

      if (parseResponse) {
        try {
          const parsed = parseResponse(outputText);
          const thinking = typeof parsed?.thinking === 'string' ? parsed.thinking : '';
          const answer = typeof parsed?.answer === 'string' ? parsed.answer : cleanGemmaTextResponse(outputText);
          const formatted = formatGemmaThinkingResponse(thinking, answer);
          return {
            content: formatted,
            tokensUsed: Math.ceil(formatted.length / 4),
          };
        } catch (error) {
          console.warn('[Gemma4Onnx] Failed to parse thinking response; using heuristic extraction instead:', error);
        }
      }

      const fallback = extractGemmaThinkingFallback(outputText);
      const formatted = formatGemmaThinkingResponse(fallback.thinking, fallback.answer);
      return {
        content: formatted,
        tokensUsed: Math.ceil(formatted.length / 4),
      };
    }

    const content = cleanGemmaTextResponse(outputText);
    return {
      content,
      tokensUsed: Math.ceil(content.length / 4),
    };
  }

  private buildPrompt(messages: GemmaGenerateInput['messages'], enableThinking = false) {
    const normalizedMessages: GemmaMessage[] = messages.map((message) => {
      if (typeof message.content === 'string') {
        return {
          role: message.role,
          content: [{ type: 'text', text: message.content }],
        };
      }

      return {
        role: message.role,
        content: message.content.map((part) => {
          if (part.type === 'text') {
            return { type: 'text', text: part.text || '' };
          }

          if (part.type === 'image_url') {
            return { type: 'image', source: part.image_url?.url };
          }

          return { type: 'text', text: '' };
        }),
      };
    });

    try {
      const tokenizer = this.processor?.tokenizer ?? this.tokenizer ?? this.processor;
      if (tokenizer?.apply_chat_template) {
        return tokenizer.apply_chat_template(normalizedMessages, {
          tokenize: false,
          add_generation_prompt: true,
          enable_thinking: enableThinking,
        });
      }
    } catch (error) {
      console.warn('[Gemma4Onnx] Falling back to manual prompt construction:', error);
    }

    const transcript = normalizedMessages
      .map((message) => {
        const text = message.content
          .map((part) => {
            if (part.type === 'text') return part.text || '';
            if (part.type === 'image') return '[image]';
            if (part.type === 'audio') return '[audio]';
            return '';
          })
          .filter(Boolean)
          .join('\n');

        return `${message.role.toUpperCase()}: ${text}`;
      })
      .join('\n\n');

    if (enableThinking) {
      return [
        'SYSTEM: Think step by step before answering. Show your reasoning under a "Thinking" section, then provide a concise final answer under an "Answer" section.',
        transcript,
        'ASSISTANT:',
      ].join('\n\n');
    }

    return `${transcript}\n\nASSISTANT:`;
  }

  private async buildProcessorInputs(transformers: any, prompt: string, imageSources: string[], audioSources: string[]) {
    const images = await Promise.all(imageSources.map((source) => this.loadImageSource(transformers, source)));
    const audios = await Promise.all(audioSources.map((source) => this.loadAudioSource(transformers, source)));
    const hasMediaInputs = images.length > 0 || audios.length > 0;
    const textTokenizer = this.processor?.tokenizer ?? this.tokenizer;

    if (!hasMediaInputs && textTokenizer) {
      return await textTokenizer(prompt, {
        add_special_tokens: true,
        return_tensor: true,
      });
    }

    if (this.processor) {
      try {
        return await this.processor(prompt, {
          images: images.length > 0 ? images : undefined,
          audio: audios.length > 0 ? audios : undefined,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error || '');
        if (!hasMediaInputs && textTokenizer && message.includes('rgb is not a function')) {
          console.warn('[Gemma4Onnx] Processor text path failed; falling back to tokenizer-only inputs:', error);
          return await textTokenizer(prompt, {
            add_special_tokens: true,
            return_tensor: true,
          });
        }

        throw error;
      }
    }

    if (this.tokenizer) {
      return await this.tokenizer(prompt, {
        add_special_tokens: true,
        return_tensor: true,
      });
    }

    return { text_inputs: prompt };
  }

  private async loadImageSource(transformers: any, source: string) {
    if (transformers.load_image) {
      return await transformers.load_image(source);
    }

    if (transformers.loadImage) {
      return await transformers.loadImage(source);
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = source;
    await image.decode();
    return image;
  }

  private async loadAudioSource(transformers: any, source: string) {
    if (transformers.read_audio) {
      return await transformers.read_audio(source);
    }

    if (transformers.readAudio) {
      return await transformers.readAudio(source);
    }

    const response = await fetch(source);
    const arrayBuffer = await response.arrayBuffer();
    return new Float32Array(arrayBuffer);
  }
}

export const gemma4Onnx = new Gemma4OnnxManager();